/**
 * CourseMaterialUploadModal — file picker with XHR progress bar.
 * Validates file size ≤ 200MB and allowed content types before upload.
 * On success: closes modal + invalidates material list.
 */
import { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ALLOWED_CONTENT_TYPES,
  MAX_FILE_BYTES,
} from '@shared/schemas/course-material-schemas';
import {
  useUploadMaterial,
  validateMaterialFile,
  type CourseMaterial,
} from '@client/lib/queries/use-course-materials';

const FILE_TYPES = [
  { value: 'file', label: 'Tệp chung' },
  { value: 'document', label: 'Tài liệu' },
  { value: 'audio', label: 'Audio' },
  { value: 'slide', label: 'Slide' },
] as const;

const ACCEPT = ALLOWED_CONTENT_TYPES.join(',');

interface CourseMaterialUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseLessonId: string;
}

export function CourseMaterialUploadModal({
  open,
  onOpenChange,
  courseLessonId,
}: CourseMaterialUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CourseMaterial['type']>('file');

  const { upload, progress, status, error, reset } = useUploadMaterial();

  const isUploading = status === 'requesting' || status === 'uploading' || status === 'confirming';

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setFileError(null);
    if (selected) {
      const err = validateMaterialFile(selected);
      if (err) {
        setFileError(err);
        setFile(null);
      } else if (!title) {
        // Auto-fill title from filename
        setTitle(selected.name.replace(/\.[^.]+$/, ''));
      }
    }
  }

  function handleClose() {
    if (isUploading) return;
    reset();
    setFile(null);
    setFileError(null);
    setTitle('');
    setType('file');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onOpenChange(false);
  }

  async function handleUpload() {
    if (!file || !title.trim()) return;
    await upload({
      courseLessonId,
      title: title.trim(),
      type,
      file,
    });
    if (status !== 'error') {
      toast.success('Tải lên thành công!');
      handleClose();
    }
  }

  // After upload completes, close and toast
  const prevStatusRef = useRef(status);
  if (prevStatusRef.current !== status) {
    prevStatusRef.current = status;
    if (status === 'done') {
      toast.success('Tải lên thành công!');
      // Close via timeout to let state settle
      setTimeout(handleClose, 200);
    }
  }

  const progressLabel =
    status === 'requesting'
      ? 'Đang chuẩn bị…'
      : status === 'uploading'
        ? `Đang tải lên… ${progress}%`
        : status === 'confirming'
          ? 'Đang xác nhận…'
          : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>Tải lên tệp học liệu</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 mt-2">
          {/* File picker */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">
              Chọn tệp <span className="text-destructive">*</span>
            </Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Chọn tệp để tải lên"
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
              {file ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground truncate max-w-[200px]">{file.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Xóa tệp"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground text-center">
                  Nhấn để chọn tệp<br />
                  <span className="text-xs">PDF, Word, PPT, ảnh, video, audio — tối đa {MAX_FILE_BYTES / 1024 / 1024} MB</span>
                </span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-title" className="text-sm font-medium">
              Tiêu đề <span className="text-destructive">*</span>
            </Label>
            <Input
              id="upload-title"
              className="h-10"
              placeholder="Tên học liệu..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
            />
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">Phân loại</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as CourseMaterial['type'])}
              disabled={isUploading}
            >
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Progress */}
          {isUploading && (
            <div className="flex flex-col gap-1.5">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">{progressLabel}</p>
            </div>
          )}

          {/* Error from upload hook */}
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isUploading}>
              Hủy
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!file || !title.trim() || isUploading || !!fileError}
            >
              {isUploading ? progressLabel : 'Tải lên'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
